import {
  BasketIcon,
  Box,
  ClickAwayListener,
  DownIcon,
  Fade,
  GKButton,
  MutualFundOrderIcon,
  PlusIcon,
  Popper,
  Typography,
} from "components";
import React, { useState } from "react";

export type CreateOrderType = "basket" | "mutualFund";

interface CreateOrderButtonProps {
  onSelect: (orderType: CreateOrderType) => void;
  variant?: "centered" | "compact";
}

const CreateOrderButton = ({
  onSelect,
  variant = "centered",
}: CreateOrderButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleSelect = (orderType: CreateOrderType) => {
    setAnchorEl(null);
    onSelect(orderType);
  };

  const orderOptions = [
    { icon: BasketIcon, label: "Basket", value: "basket" as const },
    {
      icon: MutualFundOrderIcon,
      label: "Mutual Fund",
      value: "mutualFund" as const,
    },
  ];

  return (
    <>
      <GKButton
        variant="contained"
        startIcon={<PlusIcon height={16} width={16} />}
        endIcon={
          <Box
            sx={{
              alignItems: "center",
              display: "inline-flex",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <DownIcon height={16} width={16} />
          </Box>
        }
        onClick={handleClick}
        sx={{
          bgcolor: "#06576c",
          borderRadius: "6px",
          color: "var(--white)",
          fontSize: 12,
          fontWeight: 600,
          gap: "8px",
          height: 40,
          p: "12px",
          textTransform: "none",
          "& .MuiButton-startIcon, & .MuiButton-endIcon": { margin: 0 },
          "&:hover": { bgcolor: "#06576c" },
        }}
      >
        Create Order
      </GKButton>

      {open && (
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Popper
            anchorEl={anchorEl}
            open={open}
            placement={variant === "compact" ? "bottom-end" : "bottom"}
            transition
            sx={{ zIndex: 5 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={150}>
                <Box
                  sx={{
                    bgcolor: "var(--white)",
                    border: "1px solid var(--neutral-100)",
                    borderRadius: "8px",
                    boxShadow: "4px 4px 12px rgba(0, 0, 0, 0.25)",
                    mt: 1,
                    overflow: "hidden",
                    py: "2px",
                    width: 148,
                  }}
                >
                  {orderOptions.map((option) => (
                    <Box
                      key={option.label}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelect(option.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          handleSelect(option.value);
                        }
                      }}
                      sx={{
                        alignItems: "center",
                        bgcolor: "var(--white)",
                        color: "var(--grey-900)",
                        cursor: "pointer",
                        display: "flex",
                        gap: "8px",
                        height: 40,
                        p: "12px",
                        "&:hover": { bgcolor: "var(--neutral-50)" },
                      }}
                    >
                      <option.icon height={16} width={16} />
                      <Typography
                        fontSize={12}
                        fontWeight={600}
                        color="var(--grey-900)"
                      >
                        {option.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

export default CreateOrderButton;
